import { Action, createReducer, on } from '@ngrx/store';
import { initialSeatMapV2State } from './seat-map-v2.state';
import {
  clearSeat,
  initializeSeatMapV2State,
  resetSeatMaps,
  seatMapLookup,
  seatMapLookupComplete,
  setCurrentSeatMapHashId,
  setPassengerHashIdForAssignedSeat,
  setPassengerSortType,
  setSeatMapHashId,
  setSelectedPassengerSeat,
  setSelectedSeatMapPassengerHashId,
  staticSeatMapLookup,
} from './seat-map-v2.actions';
import { Status } from '../../../models/status';
import { Seat } from '../../../models/seat-map/seat';
import { SeatMapData } from '../../../dtos/response/seat-map-lookup-response/seat-map-data';

const featureReducer = createReducer(
  initialSeatMapV2State,
  /**
   * Set the current sorting type for seat map passenger list
   */
  on(setPassengerSortType, (state, { sortType }) => ({ ...state, currentPassengerSortType: sortType })),
  /**
   * Set the seat map selected passenger hash id
   */
  on(setSelectedSeatMapPassengerHashId, (state, { passengerHashId }) => ({ ...state, selectedPassengerHashId: passengerHashId })),
  /**
   * On the completion of the seat map lookup, update the seat map data for the current segment index
   */
  on(staticSeatMapLookup, (state, { seatMapLookupRequest }) => {
    return {
      ...state,
      seatMapLookupCrudStatus: Status.LOADING,
    };
  }),
  on(seatMapLookup, (state, { seatMapLookupRequest }) => {
    return {
      ...state,
      seatMapLookupCrudStatus: Status.LOADING,
    };
  }),
  /**
   * On the completion of the seat map lookup, update the seat map data for the current segment index
   */
  on(seatMapLookupComplete, (state, { seatMapLookupResponse }) => {
    if (!seatMapLookupResponse.success) {
      return {
        ...state,
        seatMapLookupCrudStatus: Status.FAILED,
      };
    } else {
      return {
        ...state,
        seatMaps: seatMapLookupResponse?.response?.seatMaps,
        passengerSegments: seatMapLookupResponse?.response?.passengerSegments,
        seatMapLookupCrudStatus: Status.STABLE,
      };
    }
  }),
  on(setCurrentSeatMapHashId, (state, { hashId }) => ({ ...state, currentSegmentSeatMapHashId: hashId })),
  on(resetSeatMaps, (state) => ({ ...state, seatMaps: [] })),
  on(setSelectedPassengerSeat, (state, { seat, passenger, seatMapHashId }) => {
    // Logic:
    // 1. Find the passenger originally assigned seat
    // 2. If the new seat is different than the original seat
    // 3. Remove the passenger from the previous seat but not from the original seat
    const seatMapsAfterClearingOriginalSeat = updateSeatMap(state.seatMaps, seatMapHashId, (s) =>
      s.passengerHashId === passenger.hashId ? clearSeatPassenger(s) : s
    );
    const updatedSeatMaps = updateSeatMap(seatMapsAfterClearingOriginalSeat, seatMapHashId, (s) =>
      s.hashId === seat.hashId ? assignSeatPassenger(s, passenger.hashId) : s
    );
    return {
      ...state,
      seatMaps: updatedSeatMaps,
    };
  }),
  on(clearSeat, (state, { passengerSeat }) => {
    const updatedSeatMaps = updateSeatMap(state.seatMaps, state.seatMapHashId, (seat) =>
      seat.hashId === passengerSeat.seatHashId ? clearSeatPassenger(seat) : seat
    );
    return {
      ...state,
      seatMaps: updatedSeatMaps,
    };
  }),
  on(setPassengerHashIdForAssignedSeat, (state, { seatHashId, passengerHashId }) => {
    const updatedSeatMaps = updateSeatMap(state.seatMaps, state.seatMapHashId, (seat) =>
      seat.hashId === seatHashId ? { ...seat, passengerHashId } : seat
    );
    return {
      ...state,
      seatMaps: updatedSeatMaps,
    };
  }),
  on(setSeatMapHashId, (state, { hashId }) => ({ ...state, seatMapHashId: hashId })),
  on(initializeSeatMapV2State, (state) => ({ ...state, ...initialSeatMapV2State }))
);

/**
 * Helper function to update a seat map by applying a seat transformation function
 */
function updateSeatMap(seatMaps: SeatMapData[] | undefined, targetMapHashId: string, seatTransformFn: (seat: Seat) => Seat): SeatMapData[] {
  if (!seatMaps) {
    return [];
  }

  return seatMaps.map((map) => {
    if (map.hashId !== targetMapHashId) {
      return map;
    }

    return {
      ...map,
      rows: map.rows.map((row) => ({
        ...row,
        seats: row.seats.map(seatTransformFn),
      })),
    };
  });
}

/**
 * Helper function to clear passenger from a seat
 */
function clearSeatPassenger(seat: Seat): Seat {
  return {
    ...seat,
    isChanged: true,
    passengerHashId: undefined,
    passenger: undefined,
    occupied: false,
  };
}

/**
 * Helper function to assign passenger to a seat
 */
function assignSeatPassenger(seat: Seat, passengerHashId: string): Seat {
  return {
    ...seat,
    isChanged: true,
    passengerHashId,
  };
}

export function seatMapV2Reducer(state = initialSeatMapV2State, action: Action) {
  return featureReducer(state, action);
}
